import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../../services/supabaseClient'
import type { Profile } from '../../store/authSlice'
import InlineAlert from '../base/InlineAlert'
import styles from './AdminPanel.module.css'

interface TeamManagementProps {
  profiles: Profile[]
  onRefresh: () => void
}

interface Team {
  id: string
  name: string
  description: string | null
  status: string
}

export default function TeamManagement({ profiles, onRefresh }: TeamManagementProps) {
  const [teams, setTeams] = useState<Team[]>([])
  const [teamsLoading, setTeamsLoading] = useState(true)
  const [bulkTeam, setBulkTeam] = useState<string>('')
  const [newTeamName, setNewTeamName] = useState('')
  const [newTeamDescription, setNewTeamDescription] = useState('')
  const [creating, setCreating] = useState(false)
  const [createAlert, setCreateAlert] = useState<{ variant: 'success' | 'error'; message: string } | null>(null)
  const [status, setStatus] = useState<Record<string, string>>({})
  const [teamOverrides, setTeamOverrides] = useState<Record<string, string>>({})
  const [saveAlert, setSaveAlert] = useState<{ variant: 'success' | 'error'; message: string } | null>(null)

  const clearOverride = (userId: string) =>
    setTeamOverrides((o) => {
      if (!(userId in o)) return o
      const next = { ...o }
      delete next[userId]
      return next
    })

  const loadTeams = useCallback(async () => {
    setTeamsLoading(true)
    const { data, error } = await supabase
      .from('teams')
      .select('id, name, description, status')
      .eq('status', 'active')
      .order('name', { ascending: true })
    if (!error && data) {
      setTeams(data as Team[])
      if (data.length > 0 && !bulkTeam) setBulkTeam((data[0] as Team).id)
    }
    setTeamsLoading(false)
  }, [bulkTeam])

  useEffect(() => { loadTeams() }, [loadTeams])

  const showStatus = (userId: string, msg: string) => {
    setStatus((prev) => ({ ...prev, [userId]: msg }))
    setTimeout(() => setStatus((prev) => { const n = { ...prev }; delete n[userId]; return n }), 2000)
  }

  const handleCreateTeam = async () => {
    const name = newTeamName.trim()
    if (!name) {
      setCreateAlert({ variant: 'error', message: 'Team name is required.' })
      return
    }
    setCreating(true)
    setCreateAlert(null)
    const { error } = await supabase.rpc('create_team', {
      p_name: name,
      p_description: newTeamDescription.trim() || undefined,
    })
    setCreating(false)
    if (error) {
      setCreateAlert({ variant: 'error', message: error.message })
      return
    }
    setNewTeamName('')
    setNewTeamDescription('')
    setCreateAlert({ variant: 'success', message: `Team "${name}" created.` })
    await loadTeams()
  }

  const userLabel = (userId: string) => {
    const p = profiles.find((x) => x.id === userId)
    return p?.display_name || p?.email || userId
  }

  const handleSaveTeam = async (userId: string, teamId: string) => {
    setSaveAlert(null)
    const current = profiles.find((p) => p.id === userId)?.team_id ?? null
    const target = teamId || null

    if (current === target) {
      showStatus(userId, 'No change')
      clearOverride(userId)
      return
    }

    // Clearing team
    if (!target) {
      const { data, error } = await supabase.rpc('remove_team_mate', { p_target_user_id: userId })
      if (error || data === false) {
        showStatus(userId, 'Error')
        setSaveAlert({
          variant: 'error',
          message: `Could not remove ${userLabel(userId)} from their team. ${error?.message ?? 'Backend returned false — likely a permissions or role constraint.'}`,
        })
        return
      }
      showStatus(userId, 'Removed')
      clearOverride(userId)
      onRefresh()
      return
    }

    // Team → team switch: must remove first, since assign_to_team only sets from null
    if (current && current !== target) {
      const removeRes = await supabase.rpc('remove_team_mate', { p_target_user_id: userId })
      if (removeRes.error || removeRes.data === false) {
        showStatus(userId, 'Error')
        setSaveAlert({
          variant: 'error',
          message: `Could not detach ${userLabel(userId)} from their current team. ${removeRes.error?.message ?? 'Backend returned false — they may be a leader; demote them first.'}`,
        })
        return
      }
    }

    // Assign (from null, or after a successful remove)
    const { data, error } = await supabase.rpc('assign_to_team', {
      p_user_id: userId,
      p_team_id: target,
    })
    if (error || data === false) {
      showStatus(userId, 'Error')
      setSaveAlert({
        variant: 'error',
        message: `Could not assign ${userLabel(userId)} to the selected team. ${error?.message ?? 'Backend returned false.'}${current && current !== target ? ' They are now without a team — retry to reassign.' : ''}`,
      })
      // Refresh so the UI reflects the removed-but-not-reassigned state
      onRefresh()
      return
    }
    showStatus(userId, 'Saved')
    clearOverride(userId)
    onRefresh()
  }

  const handleUpdateName = async (userId: string, name: string) => {
    await supabase
      .from('profiles')
      .update({ display_name: name })
      .eq('id', userId)
  }

  const handleBulkAssign = async () => {
    if (!bulkTeam) return
    setSaveAlert(null)
    const failures: string[] = []
    for (const p of profiles) {
      if (p.team_id === bulkTeam) continue
      if (p.team_id && p.team_id !== bulkTeam) {
        const rm = await supabase.rpc('remove_team_mate', { p_target_user_id: p.id })
        if (rm.error || rm.data === false) {
          failures.push(`${userLabel(p.id)} (${rm.error?.message ?? 'detach refused — likely a leader'})`)
          continue
        }
      }
      const { data, error } = await supabase.rpc('assign_to_team', {
        p_user_id: p.id,
        p_team_id: bulkTeam,
      })
      if (error || data === false) {
        failures.push(`${userLabel(p.id)} (${error?.message ?? 'assign refused'})`)
      }
    }
    setTeamOverrides({})
    onRefresh()
    if (failures.length > 0) {
      setSaveAlert({
        variant: 'error',
        message: `Bulk assign: ${failures.length} user(s) could not be moved. ${failures.slice(0, 3).join('; ')}${failures.length > 3 ? ` (+${failures.length - 3} more)` : ''}`,
      })
    } else {
      setSaveAlert({ variant: 'success', message: 'All users assigned to the selected team.' })
    }
  }

  const handlePromote = async (userId: string, teamId: string | null) => {
    if (!teamId) return
    setSaveAlert(null)
    const { data, error } = await supabase.rpc('promote_to_leader', {
      p_user_id: userId,
      p_team_id: teamId,
    })
    if (error || data === false) {
      showStatus(userId, 'Error')
      setSaveAlert({
        variant: 'error',
        message: `Could not promote ${userLabel(userId)}. ${error?.message ?? 'Backend returned false.'}`,
      })
      return
    }
    showStatus(userId, 'Promoted')
    clearOverride(userId)
    onRefresh()
  }

  const handleDemote = async (userId: string) => {
    setSaveAlert(null)
    const { data, error } = await supabase.rpc('demote_to_team_mate', { p_user_id: userId })
    if (error || data === false) {
      showStatus(userId, 'Error')
      setSaveAlert({
        variant: 'error',
        message: `Could not demote ${userLabel(userId)}. ${error?.message ?? 'Backend returned false.'}`,
      })
      return
    }
    showStatus(userId, 'Demoted')
    clearOverride(userId)
    onRefresh()
  }

  const handleRemoveFromTeam = async (userId: string) => {
    setSaveAlert(null)
    const { data, error } = await supabase.rpc('remove_team_mate', { p_target_user_id: userId })
    if (error || data === false) {
      showStatus(userId, 'Error')
      setSaveAlert({
        variant: 'error',
        message: `Could not remove ${userLabel(userId)} from their team. ${error?.message ?? 'Backend returned false — they may be a leader; demote them first.'}`,
      })
      return
    }
    showStatus(userId, 'Removed')
    clearOverride(userId)
    onRefresh()
  }

  const teamName = (teamId: string | null) =>
    teamId ? teams.find((t) => t.id === teamId)?.name ?? teamId : ''

  return (
    <div className={styles.card} data-testid="team-management">
      <div className={styles.hdrOrange}>Team Management</div>

      <div className={styles.createTeamRow} data-testid="create-team-row">
        <input
          className={styles.inlineInput}
          placeholder="New team name"
          value={newTeamName}
          onChange={(e) => setNewTeamName(e.target.value)}
          data-testid="create-team-name-input"
        />
        <input
          className={styles.inlineInputWide}
          placeholder="Description (optional)"
          value={newTeamDescription}
          onChange={(e) => setNewTeamDescription(e.target.value)}
          data-testid="create-team-description-input"
        />
        <button
          className={styles.btnPrimary}
          onClick={handleCreateTeam}
          disabled={creating || !newTeamName.trim()}
          data-testid="create-team-btn"
        >
          {creating ? 'Creating...' : 'Create Team'}
        </button>
      </div>
      {createAlert && (
        <div style={{ marginBottom: 12 }} data-testid="create-team-alert">
          <InlineAlert
            message={createAlert.message}
            variant={createAlert.variant}
            onDismiss={() => setCreateAlert(null)}
          />
        </div>
      )}
      {saveAlert && (
        <div style={{ marginBottom: 12 }} data-testid="team-save-alert">
          <InlineAlert
            message={saveAlert.message}
            variant={saveAlert.variant}
            onDismiss={() => setSaveAlert(null)}
          />
        </div>
      )}

      <div className={styles.bulkRow}>
        <span className={styles.bulkLabel}>Bulk assign all users to:</span>
        <select
          className={styles.inlineSelect}
          value={bulkTeam}
          onChange={(e) => setBulkTeam(e.target.value)}
          data-testid="bulk-team-select"
          disabled={teamsLoading || teams.length === 0}
        >
          {teams.length === 0 && <option value="">— No teams —</option>}
          {teams.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        <button
          className={styles.btnPrimary}
          onClick={handleBulkAssign}
          disabled={!bulkTeam}
          data-testid="bulk-assign-btn"
        >
          Assign All
        </button>
      </div>

      <table className={styles.table} data-testid="team-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Team</th>
            <th>Role</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {profiles.map((p) => (
            <tr key={p.id}>
              <td>
                <input
                  className={styles.inlineInput}
                  defaultValue={p.display_name ?? ''}
                  onBlur={(e) => handleUpdateName(p.id, e.target.value)}
                  data-testid={`admin-name-${p.id}`}
                />
              </td>
              <td className={styles.emailCell}>{p.email}</td>
              <td>
                <select
                  className={styles.inlineSelect}
                  value={teamOverrides[p.id] ?? p.team_id ?? ''}
                  onChange={(e) => setTeamOverrides((o) => ({ ...o, [p.id]: e.target.value }))}
                  data-testid={`admin-team-${p.id}`}
                  title={teamName(p.team_id)}
                >
                  <option value="">— No team —</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                  {/* If the user's current team isn't in the active teams list (e.g. archived), keep it visible */}
                  {p.team_id && !teams.some((t) => t.id === p.team_id) && (
                    <option value={p.team_id}>{p.team_id} (archived)</option>
                  )}
                </select>
              </td>
              <td>
                <span
                  className={`role-badge ${p.app_role === 'admin' ? 'role-badge-admin' : p.app_role === 'leader' ? 'role-badge-leader' : ''}`}
                  data-testid={`admin-role-${p.id}`}
                >
                  {p.app_role}
                </span>
              </td>
              <td className={styles.actionCell}>
                <div className={styles.actionButtons}>
                  <button
                    className={styles.btnSm}
                    onClick={() => handleSaveTeam(p.id, teamOverrides[p.id] ?? p.team_id ?? '')}
                    data-testid={`admin-save-${p.id}`}
                  >
                    {status[p.id] ?? 'Save'}
                  </button>
                  {p.app_role === 'team_mate' && (
                    <button
                      className={styles.btnSm}
                      onClick={() => handlePromote(p.id, p.team_id)}
                      disabled={!p.team_id}
                      title={p.team_id ? 'Promote to leader' : 'Assign a team first'}
                      data-testid={`admin-promote-${p.id}`}
                    >
                      Promote
                    </button>
                  )}
                  {p.app_role === 'leader' && (
                    <button
                      className={styles.btnSm}
                      onClick={() => handleDemote(p.id)}
                      data-testid={`admin-demote-${p.id}`}
                    >
                      Demote
                    </button>
                  )}
                  {p.team_id && p.app_role !== 'admin' && (
                    <button
                      className={styles.btnSm}
                      onClick={() => handleRemoveFromTeam(p.id)}
                      title="Remove from team"
                      data-testid={`admin-remove-${p.id}`}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
